import type { LensFilterValues } from '~/types/leLens';

import { csvCell } from '~/utils/csvCell';
import { lensDataReady } from '~/utils/leLensTransport';

import { CHART_YEARS, calendarWindow, globalFinancialRows, groupCalendar } from './calendar';
import cardSchema from './cardSchema.json';
import { config } from './config';
import {
  type CMPageContext,
  type ReportState,
  type ReportView,
  addDays,
  comparisonWindow,
  currentWindow,
  focusMarkets,
  metricsFor,
  parseReport,
  priorWindow,
  reportDefaults,
  reportMarkets,
  reportViews,
  selectionComparisons,
} from './fidelity';
import {
  LEGACY_WINDOW,
  completedFinancialMonth,
  financialMonthExpectation,
  financialReadings,
  legacyReadings,
} from './legacy';
import { type Reading, dateError, numeric, reportCalendar, rowsFor } from './liveModel';
import { type Metrics, growth, ratio, validDate } from './presentationModel';

type Query = Parameters<typeof lensDataReady>[0];
const DAILY_DEFAULT_DAYS = 14;
function defaultWindows(now: Date) {
  const day = completedFinancialMonth(now).to;
  return {
    lastDays: { from: addDays(day, 1 - DAILY_DEFAULT_DAYS), to: day },
    lastSixMonths: {
      from: new Date(Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 6, 1)).toISOString().slice(0, 10),
      to: day,
    },
  };
}
export function defaultsAt(now: Date, view: ReportView = reportDefaults.view): ReportState {
  const { lastDays, lastSixMonths } = defaultWindows(now),
    day = lastSixMonths.to;
  return {
    ...reportDefaults,
    view,
    ...(view === 'Daily CM Tracking' ? lastDays : lastSixMonths),
    selectedDate: day,
    year: CHART_YEARS.includes(day.slice(0, 4)) ? day.slice(0, 4) : CHART_YEARS[CHART_YEARS.length - 1],
  };
}
export function readContributionState(search: string, now: Date): ReportState {
  const p = new URLSearchParams(search);
  const aliases: Record<string, string> = {
    daily: 'Daily CM Tracking',
    'global-monthly': 'Global Monthly CM Tracking',
    'regions-monthly': 'Regions Monthly CM Tracking',
    comparison: 'Comparision Overview',
    'comparison-chart': 'Comparision Chart',
    mtd: 'MTD Expectation',
    'global-mtd': 'MTD Expectation',
    channels: 'Source health',
    legacy: 'Sheet 68 · legacy bank fees',
  };
  for (const key of [
    'from',
    'to',
    'basis',
    'master',
    'cadence',
    'selectedDate',
    'selectionComparison',
    'year',
    'legacyRegion',
  ]) {
    if (!p.has('cm' + key) && p.has(key)) {
      p.set('cm' + key, p.get(key));
    }
  }
  const oldView = p.get('reportView') ?? p.get('view');
  if (!p.has('cmview') && oldView) {
    p.set('cmview', aliases[oldView] ?? oldView);
  }
  if (!p.has('cmmarket') && p.has('region')) {
    p.set('cmmarket', p.getAll('region').filter(Boolean).join('|') || 'Global - All Markets');
  }
  const state = parseReport(p.toString(), 'cm'),
    base = defaultsAt(now, state.view);
  for (const key of Object.keys(base) as Array<keyof ReportState>) {
    if (!p.has('cm' + key)) {
      Object.assign(state, { [key]: base[key] });
    }
  }
  return state;
}
export function contributionError(s: ReportState, now: Date): string | null {
  if (!reportViews.includes(s.view)) {
    return 'Choose one of the report views.';
  }
  const invalid = dateError(s.from, s.to);
  if (invalid || !validDate(s.from) || !validDate(s.to)) {
    return invalid ?? 'Choose valid civil dates.';
  }
  if (!reportMarkets.includes(s.legacyRegion)) {
    return 'Choose a source legacy bank-fee region.';
  }
  if (!reportMarkets.includes(s.market)) {
    return 'Choose a source market.';
  }
  if (!['Previous Year', 'Previous Period', 'Previous Month', 'Previous Quarter'].includes(s.comparison)) {
    return 'Choose a comparison period.';
  }
  if (!['Net CM', 'Gross CM'].includes(s.basis)) {
    return 'Choose a margin basis.';
  }
  if (!['Custom Date Range', 'Date Selection'].includes(s.master)) {
    return 'Choose a date mode.';
  }
  if (s.master === 'Date Selection' && !selectionComparisons[s.cadence]?.includes(s.selectionComparison)) {
    return 'Choose a comparison matching the selected cadence.';
  }
  if (!validDate(s.selectedDate)) {
    return 'Choose a valid selected date.';
  }
  if (!CHART_YEARS.includes(s.year)) {
    return 'Choose a sourced calendar year.';
  }
  if (!['Daily', 'Weekly', 'Monthly', 'Monthly - MTD', 'Quarterly', 'Yearly', 'Yearly - YTD'].includes(s.cadence)) {
    return 'Choose a supported cadence.';
  }

  if (
    scope(s, now).to > completedFinancialMonth(now).to &&
    s.view !== 'Comparision Overview' &&
    s.view !== 'Comparision Chart'
  ) {
    return 'Choose completed Sydney dates.';
  }
  return null;
}
export function scope(s: ReportState, now: Date) {
  if (s.view === 'Sheet 68 · legacy bank fees') {
    return LEGACY_WINDOW;
  }
  if (s.view === 'MTD Expectation') {
    return completedFinancialMonth(now);
  }
  if (s.view === 'Comparision Chart') {
    return calendarWindow(s.year);
  }
  const [from, to] = s.view === 'Comparision Overview' ? currentWindow(s) : [s.from, s.to];
  return { from, to };
}
export function contributionRequest(s: ReportState, now: Date): LensFilterValues {
  if (contributionError(s, now)) {
    return {};
  }
  const window = scope(s, now),
    [from, to] = comparisonWindow(s);
  const compare = ['Comparision Chart', 'Comparision Overview'].includes(s.view);
  return {
    period: {
      window,
      comparison: compare ? (s.view === 'Comparision Chart' ? calendarWindow(s.year, true) : { from, to }) : null,
    },
    region:
      ['Global Monthly CM Tracking', 'Regions Monthly CM Tracking', 'MTD Expectation'].includes(s.view) ||
      s.market === 'Global - All Markets'
        ? []
        : [s.market],
    cm_today: reportCalendar(now).today,
    chart_year: s.year,
    chart_grain: 'month',
  };
}
const aliases: Record<string, string> = {
  bank: 'bank_fees',
  gm: 'margin_aud_ar',
  gross: 'gross_cm',
  comparisonNet: 'comparison_cm',
  net: 'net_cm',
  netMargin: 'net_margin',
  gmRate: 'gm_rate',
  roas: 'margin_roas',
  ttvRoas: 'ttv_roas',
  refundRate: 'refund_rate',
};
export function presentationMetrics(row: Reading | undefined): Metrics {
  if (!row) {
    return {};
  }
  const result: Metrics = Object.fromEntries(Object.entries(row).map(([key, value]) => [key, numeric(value)]));
  for (const [key, source] of Object.entries(aliases)) {
    result[key] = numeric(row[source]);
  }
  result.ttvOthers =
    result.gross_sales_aud_br === null || result.cash_sales_aud === null || result.credit_sales_aud === null
      ? null
      : result.gross_sales_aud_br - result.cash_sales_aud - result.credit_sales_aud;
  result.refundOthers =
    result.total_refund_aud === null || result.cash_refund_aud === null || result.credit_refund_aud === null
      ? null
      : result.total_refund_aud - result.cash_refund_aud - result.credit_refund_aud;
  // The workbook's "Net Margin ROAS" divides GM (AR) by spend (kept as GM (AR) ROAS); this is net margin over spend,
  // with the same zero-spend handling as the other ROAS rows.
  result.netMarginRoas =
    result.netMargin === null || result.cost_aud === null
      ? null
      : result.netMargin / (result.cost_aud === 0 ? 1 : result.cost_aud);
  result.target = result.targetVariance = result.targetAchievement = null;
  return result;
}
export function contributionCardIds(s: ReportState): string[] {
  if (s.view === 'Sheet 68 · legacy bank fees') return ['legacy'];
  if (s.view === 'Comparision Overview') return ['summary', 'global_summary'];
  if (s.view === 'Comparision Chart') return [s.market === 'Global - All Markets' ? 'calendar_global' : 'calendar'];
  if (s.view === 'MTD Expectation') return ['summary', 'global_summary', 'channel_cells', 'targets'];
  if (s.view === 'Source health') return ['channel_cells'];
  if (s.view === 'Daily CM Tracking') return ['daily', 'channel_cells'];
  return s.view === 'Global Monthly CM Tracking'
    ? ['global_monthly', 'channel_cells', 'targets']
    : ['monthly', 'channel_cells', 'targets'];
}
/**
 * Like-for-like scope for the Comparison Chart's current, still partial month: month to date against the
 * same days last year. The workbook compared the partial month with the full prior-year month, so the
 * current month always read as a steep decline (e.g. 1–20 Oct against all of last October).
 */
export function chartMtdRequest(s: ReportState, now: Date): LensFilterValues | null {
  if (s.view !== 'Comparision Chart' || contributionError(s, now)) return null;
  const window = completedFinancialMonth(now);
  if (window.from.slice(0, 4) !== s.year) return null;
  const [from, to] = priorWindow(window.from, window.to, 'Previous Year');
  return { ...contributionRequest(s, now), period: { window, comparison: { from, to } } };
}
export function chartMtdCardIds(s: ReportState): string[] {
  return [s.market === 'Global - All Markets' ? 'global_summary' : 'summary'];
}
export const LIKE_FOR_LIKE_SCOPE = 'MTD vs same days LY';
/** Growth for the current month bar; null until both month-to-date scopes have settled. */
export function likeForLikeGrowth(mtd: Metrics | null, mtdPrior: Metrics | null) {
  return {
    growthScope: LIKE_FOR_LIKE_SCOPE,
    priorMtdCm: mtdPrior?.comparisonNet ?? null,
    priorMtdGm: mtdPrior?.gm ?? null,
    cmGrowth: mtd && mtdPrior ? growth(mtd.comparisonNet ?? null, mtdPrior.comparisonNet ?? null) : null,
    gmGrowth: mtd && mtdPrior ? growth(mtd.gm ?? null, mtdPrior.gm ?? null) : null,
  };
}
export function adaptContribution(s: ReportState, query: Query, now: Date, mtdQuery?: Query) {
  const cards = query.cards ?? [],
    card = (id: string) => cards.find((c) => c.cardId === id);
  const isCompare = ['Comparision Overview', 'Comparision Chart'].includes(s.view);
  const required = contributionCardIds(s);
  const error = contributionError(s, now);
  const cells = decodeChannelCells(card('channel_cells'));
  // Targets come from a separate legacy source; when it fails, actuals still render and targets read Unknown.
  const targetsCard = card('targets');
  const targetsFailed = targetsCard?.status === 'error' || targetsCard?.status === 'restricted';
  const readyCards = targetsFailed ? required.filter((id) => id !== 'targets') : required;
  const sourceError =
    readyCards.map((id) => card(id)).find((c) => c?.status === 'error' || c?.status === 'restricted')?.message ?? null;
  const targets = targetsFailed ? [] : (targetsCard?.rows ?? []);
  const targetsValid = targets.every(
    (row) =>
      typeof row.month === 'string' &&
      validDate(row.month) &&
      typeof row.region === 'string' &&
      Object.prototype.hasOwnProperty.call(row, 'net_contribution_margin_target') &&
      (row.net_contribution_margin_target === null || numeric(row.net_contribution_margin_target) !== null),
  );
  const ready = lensDataReady(
    query,
    readyCards,
    !error &&
      validFinancialCards(cards, readyCards, isCompare) &&
      (!readyCards.includes('targets') || targetsValid) &&
      (!readyCards.includes('channel_cells') || cells !== null),
    isCompare ? readyCards : [],
  );
  const read = (id: string, prior = false): Array<Reading> => (ready ? rowsFor(card(id), config.ratios, prior) : []);
  const financial = (id: string, prior = false) => financialReadings(read(id, prior));
  const regionOptions = [...new Set(
    read(s.view === 'MTD Expectation' ? 'summary' : 'monthly')
      .map((row) => row.region)
      .filter((region): region is string => typeof region === 'string' && !!region && !region.includes(',')),
  )].sort();
  const selectedRegions = (state: ReportState) =>
    state.regions
      ? state.regions.split(',')
      : state.market !== 'Global - All Markets'
        ? [state.market]
        : state.view === 'MTD Expectation' ? ['AU'] : regionOptions;
  const select = (rows: Array<Reading>, market: string) =>
    market === 'Global - All Markets' ? rows : rows.filter((r) => r.region === market);
  const summary = (market: string, prior = false) =>
    presentationMetrics(
      market === 'Global - All Markets'
        ? ready
          ? globalFinancialRows(card('global_summary'), prior)[0]
          : undefined
        : financial('summary', prior).find((r) => r.region === market),
    );
  const metadata = {
    source: config.source,
    from: scope(s, now).from,
    to: completedFinancialMonth(now).to,
    extractedAt:
      required
        .map((id) => card(id)?.refreshedAt)
        .filter(Boolean)
        .sort()
        .slice(-1)[0] ?? '',
    tableModifiedAt: 'Unknown',
  };
  const withTarget = (metrics: Metrics, month: string, market: string): Metrics => {
    const matching = targets.filter(
      (row) =>
        typeof row.month === 'string' &&
        row.month.slice(0, 7) === month &&
        (market === 'Global - All Markets'
          ? !['US', 'EU', 'UK'].includes(String(row.region))
          : ['US', 'EU', 'UK'].includes(market) && row.region === market),
    );
    const values = matching.map((row) => numeric(row.net_contribution_margin_target));
    const target =
      ready && values.length && values.every((value) => value !== null)
        ? values.reduce<number>((total, value) => total + (value ?? 0), 0)
        : null;
    return {
      ...metrics,
      target,
      targetVariance: metrics.net == null || target === null ? null : metrics.net - target,
      targetAchievement: ratio(metrics.net ?? null, target),
    };
  };
  const matrixColumns = (state: ReportState, market = state.market) => {
    const daily = state.view === 'Daily CM Tracking',
      global = market === 'Global - All Markets';
    let rows = read(daily ? 'daily' : global ? 'global_monthly' : 'monthly');
    rows = global && daily ? groupOperands(rows, 'transaction_date_sydt') : select(rows, market);
    return financialReadings(rows)
      .map((row) => ({
        date: String(row.transaction_date_sydt ?? '').slice(0, daily ? 10 : 7),
        metrics: {
          ...(!daily
            ? withTarget(presentationMetrics(row), String(row.transaction_date_sydt).slice(0, 7), market)
            : presentationMetrics(row)),
          ...channelMetrics(cells ?? [], String(row.transaction_date_sydt ?? '').slice(0, daily ? 10 : 7), market),
        },
      }))
      .sort((a, b) => b.date.localeCompare(a.date));
  };
  const overview = (state: ReportState) =>
    (state.market === 'Global - All Markets' ? focusMarkets : [state.market])
      .map((market) => {
        const a = summary(market),
          b = summary(market, true),
          key = state.basis === 'Gross CM' ? 'gross' : 'comparisonNet';
        const current = a[key] ?? null,
          previous = b[key] ?? null,
          gm = a.gm ?? null,
          priorGm = b.gm ?? null;
        return {
          market,
          current,
          previous,
          gm,
          priorGm,
          cmGrowth: growth(current, previous),
          gmGrowth: growth(gm, priorGm),
          index: ratio(growth(current, previous), growth(gm, priorGm)),
        };
      })
      .sort((a, b) => (state.sort === 'GM size' ? (b.gm ?? -Infinity) - (a.gm ?? -Infinity) : 0));
  const mtdMonth = mtdQuery && chartMtdRequest(s, now) ? completedFinancialMonth(now).from.slice(5, 7) : null;
  const mtdIds = chartMtdCardIds(s);
  const mtdReady =
    !!mtdQuery && lensDataReady(mtdQuery, mtdIds, validFinancialCards(mtdQuery.cards ?? [], mtdIds, true), mtdIds);
  const mtdMetrics = (prior: boolean) => {
    const mtdCard = (mtdQuery?.cards ?? []).find((c) => c.cardId === mtdIds[0]);
    return presentationMetrics(
      s.market === 'Global - All Markets'
        ? globalFinancialRows(mtdCard, prior)[0]
        : financialReadings(rowsFor(mtdCard, config.ratios, prior)).find((r) => r.region === s.market),
    );
  };
  const chartSeries = (state: ReportState) => {
    const id = state.market === 'Global - All Markets' ? 'calendar_global' : 'calendar';
    const current = financialReadings(groupCalendar(read(id)), undefined, 'chart-current'),
      prior = financialReadings(groupCalendar(read(id, true)));
    // Authoritative 7.1 Chart shelves are MONTH(Date); the retained cadence parameter drives Date Selection worksheets, not these shelves.
    const keys = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
    return keys.map((period) => {
      const a = presentationMetrics(current.find((r) => String(r.chart_bucket).slice(5, 7) === period)),
        b = presentationMetrics(prior.find((r) => String(r.chart_bucket).slice(5, 7) === period));
      return {
        period,
        label: new Date('2000-' + period + '-01T00:00:00Z').toLocaleDateString('en-AU', {
          month: 'long',
          timeZone: 'UTC',
        }),
        currentCm: a.comparisonNet ?? null,
        priorCm: b.comparisonNet ?? null,
        currentGm: current.some((r) => String(r.chart_bucket).slice(5, 7) === period) ? (a.gm ?? 0) : null,
        priorGm: prior.some((r) => String(r.chart_bucket).slice(5, 7) === period) ? (b.gm ?? 0) : null,
        cmGrowth: growth(a.comparisonNet ?? null, b.comparisonNet ?? null),
        gmGrowth: growth(
          current.some((r) => String(r.chart_bucket).slice(5, 7) === period) ? (a.gm ?? 0) : null,
          prior.some((r) => String(r.chart_bucket).slice(5, 7) === period) ? (b.gm ?? 0) : null,
        ),
        // The partial current month's growth is month to date against the same days last year.
        ...(period === mtdMonth
          ? likeForLikeGrowth(mtdReady ? mtdMetrics(false) : null, mtdReady ? mtdMetrics(true) : null)
          : {}),
      };
    });
  };
  const projected = (_state: ReportState, market: string) => {
    const window = completedFinancialMonth(now);
    const rows =
      market === 'Global - All Markets'
        ? read('global_summary')
            .filter((r) => (numeric(r.source_row_count) ?? 0) > 0)
            .map((r) => ({ ...r, region: market }))
        : select(read('summary'), market);
    const metrics = presentationMetrics(financialMonthExpectation(rows, window.from, window.to)[0]);
    const factor =
      new Date(Number(window.to.slice(0, 4)), Number(window.to.slice(5, 7)), 0).getDate() / Number(window.to.slice(8));
    return withTarget(
      {
        ...metrics,
        ...Object.fromEntries(
          Object.entries(channelMetrics(cells ?? [], window.to.slice(0, 7), market)).map(([key, value]) => [
            key,
            value === null ? null : value * factor,
          ]),
        ),
      },
      window.to.slice(0, 7),
      market,
    );
  };
  const health = (_filters: unknown) => {
    const selected = (ready ? (cells ?? []) : []).filter(
      (row) => s.market === 'Global - All Markets' || row.region === s.market,
    );
    const groups = new Map<string, Array<ChannelCell>>();
    for (const cell of selected) {
      const key = JSON.stringify([cell.region, cell.channel]);
      groups.set(key, [...(groups.get(key) ?? []), cell]);
    }
    return [...groups.values()]
      .map((rows) => ({
        market: rows[0].region,
        channel: rows[0].channel,
        first: rows.map((r) => r.date).sort()[0],
        last: rows
          .map((r) => r.date)
          .sort()
          .slice(-1)[0],
        days: new Set(rows.map((r) => r.date)).size,
        zeros: new Set(rows.filter((r) => r.zero_observations > 0).map((r) => r.date)).size,
        cost: rows.reduce((a, r) => a + r.cost, 0),
      }))
      .sort(
        (a, b) =>
          Number(b.channel === 'Manual Markeing Spend') - Number(a.channel === 'Manual Markeing Spend') ||
          b.cost - a.cost,
      );
  };
  const pageContext = (state: ReportState): CMPageContext => ({
    report: config.title,
    view: state.view,
    filters: state,
    source: config.source,
    snapshotThrough: metadata.to,
    extractedAt: metadata.extractedAt,
    metrics: ready
      ? state.view === 'MTD Expectation'
        ? projected(state, state.market)
        : state.view === 'Comparision Overview'
          ? summary(state.market)
          : {}
      : {},
    limitations: [
      'Monthly and MTD targets use the Finance FY27 CM Budget (Group tab CM Target, as in Tableau) at the source month and region grain. Missing months/regions remain Unknown; no daily target is defined by the workbook.',
      'Live source refresh timing can differ from Tableau extracts; manual-spend refresh equivalence remains unverified.',
      'No order/customer detail identifiers or original external actions are declared by the source.',
    ],
  });
  const reportCsv = (state: ReportState) => {
    if (!ready || state !== s) {
      throw new Error('Complete settled current-scope data is required for export.');
    }
    const rows: Array<Array<unknown>> = [
      ['Source', metadata.source],
      ['Source through', metadata.to],
      ['From', scope(s, now).from],
      ['To', scope(s, now).to],
      ['View', state.view],
      ['Target source', 'Finance FY27 CM Budget (Group tab CM Target), FY25-FY26 as in Tableau cm_targets'],
    ];
    if (state.view === 'Sheet 68 · legacy bank fees') {
      const values = legacyReadings(read('legacy'), state.legacyRegion, now);
      const keys = [...new Set(values.flatMap((row) => Object.keys(row)))];
      rows.push(keys, ...values.map((row) => keys.map((key) => row[key])));
    } else if (state.view === 'Comparision Overview') {
      const values = overview(state);
      const keys = Object.keys(values[0] ?? {});
      rows.push(keys, ...values.map((r) => keys.map((k) => r[k])));
    } else if (state.view === 'Comparision Chart') {
      const values = chartSeries(state);
      const keys = Object.keys(values[0] ?? {});
      rows.push(keys, ...values.map((r) => keys.map((k) => r[k])));
    } else if (state.view === 'Source health') {
      const values = health(null);
      const keys = Object.keys(values[0] ?? {});
      rows.push(keys, ...values.map((r) => keys.map((k) => r[k])));
    } else {
      for (const market of state.view === 'MTD Expectation'
        ? ['Global - All Markets', ...selectedRegions(state)]
        : state.view === 'Regions Monthly CM Tracking'
          ? selectedRegions(state)
          : [state.view === 'Global Monthly CM Tracking' ? 'Global - All Markets' : state.market]) {
        const cols =
          state.view === 'MTD Expectation'
            ? [{ date: 'Month expectation', metrics: projected(state, market) }]
            : matrixColumns(state, market);
        rows.push(
          ['Market', market],
          ['Metric', 'Format', ...cols.map((c) => c.date)],
          ...metricsFor(state.view).map((m) => [m.label, m.format, ...cols.map((c) => c.metrics[m.key])]),
        );
      }
    }
    return '\uFEFF' + rows.map((row) => row.map((value) => csvCell(value, 'Unknown')).join(',')).join('\r\n');
  };
  const legacyRows = () => legacyReadings(read('legacy'), s.legacyRegion, now);
  const exportRows = (): Array<Reading> => {
    if (!ready) {
      return [];
    }
    if (s.view === 'Sheet 68 · legacy bank fees') {
      return legacyRows();
    }
    if (s.view === 'Comparision Overview') {
      return overview(s);
    }
    if (s.view === 'Comparision Chart') {
      return chartSeries(s);
    }
    if (s.view === 'Source health') {
      return health(null);
    }
    const markets =
      s.view === 'MTD Expectation'
        ? ['Global - All Markets', ...selectedRegions(s)]
        : s.view === 'Regions Monthly CM Tracking'
          ? selectedRegions(s)
          : [s.view === 'Global Monthly CM Tracking' ? 'Global - All Markets' : s.market];
    return markets.flatMap((market) =>
      s.view === 'MTD Expectation'
        ? [{ ...projected(s, market), region: market, period: 'Month expectation' }]
        : matrixColumns(s, market).map((row) => ({ ...row.metrics, region: market, period: row.date })),
    );
  };
  const day = completedFinancialMonth(now).to,
    year = Number(day.slice(0, 4)),
    month = Number(day.slice(5, 7));
  const previousMonth = {
    from: new Date(Date.UTC(year, month - 2, 1)).toISOString().slice(0, 10),
    to: new Date(Date.UTC(year, month - 1, 0)).toISOString().slice(0, 10),
  };
  // A request still in flight is loading, not missing: only a settled response without complete data is unavailable.
  const inFlight = (q?: Query) =>
    !!q && !q.isError && (q.isFetching || q.isShowingPreviousData || q.requestIdentity !== q.responseIdentity);
  const loading = !ready && !error && !sourceError && (inFlight(query) || inFlight(mtdQuery));
  return {
    ...defaultWindows(now),
    previousMonth,
    regionOptions,
    selectedRegions,
    exportRows,
    legacyRows,
    ready,
    loading,
    metadata,
    matrixColumns,
    overview,
    chartSeries,
    projected,
    health,
    pageContext,
    reportCsv,
    reportDefaults: (view?: ReportView) => defaultsAt(now, view),
    parseReport: (search: string, _prefix?: string) => readContributionState(search, now),
    reportError: (_state: ReportState) =>
      error ??
      (ready
        ? null
        : sourceError
          ? `Source query failed: ${sourceError}`
          : loading
            ? 'Loading Contribution Margin data…'
            : 'Complete settled current-scope data is unavailable.'),
  };
}
function groupOperands(rows: Array<Reading>, key: string): Array<Reading> {
  const groups = new Map<string, Reading>();
  for (const row of rows) {
    const bucket = String(row[key]);
    const total = groups.get(bucket) ?? { [key]: bucket };
    for (const [field, value] of Object.entries(row)) {
      if (field === key || field === 'region' || config.ratios[field]) {
        continue;
      }
      const n = numeric(value);
      if (!(field in total)) {
        total[field] = null;
      }
      if (n !== null) {
        total[field] = (numeric(total[field]) ?? 0) + n;
      }
    }
    groups.set(bucket, total);
  }
  return [...groups.values()];
}
export type ContributionModel = ReturnType<typeof adaptContribution>;

type ChannelCell = {
  date: string;
  region: string;
  channel: string;
  cost: number;
  observations: number;
  zero_observations: number;
};
export function decodeChannelCells(card: import('~/types/leLens').LensCardData | undefined): Array<ChannelCell> | null {
  if (!card || !['ready', 'empty'].includes(card.status) || card.truncated) {
    return null;
  }
  const batches = [...(card.rows ?? [])].sort((a, b) => Number(a.batch) - Number(b.batch));
  if (!batches.length) {
    return [];
  }
  const all: Array<ChannelCell> = [];
  let sourceCount: number | undefined;
  for (let i = 0; i < batches.length; i++) {
    const row = batches[i],
      count = Number(row.batch_count),
      source = Number(row.source_count);
    if (
      Number(row.batch) !== i ||
      !Number.isSafeInteger(count) ||
      count < 1 ||
      count > 500 ||
      !Number.isSafeInteger(source) ||
      source < 1 ||
      (sourceCount !== undefined && sourceCount !== source) ||
      typeof row.cells_json !== 'string'
    ) {
      return null;
    }
    sourceCount = source;
    let decoded: unknown;
    try {
      decoded = JSON.parse(row.cells_json);
    } catch {
      return null;
    }
    if (!Array.isArray(decoded) || decoded.length !== count) {
      return null;
    }
    for (const cell of decoded) {
      if (!cell || typeof cell !== 'object' || Array.isArray(cell)) {
        return null;
      }
      const { date, region, channel, cost, observations, zero_observations } = cell;
      if (
        typeof date !== 'string' ||
        !validDate(date) ||
        typeof region !== 'string' ||
        typeof channel !== 'string' ||
        typeof cost !== 'number' ||
        !Number.isFinite(cost) ||
        !Number.isSafeInteger(observations) ||
        observations < 1 ||
        !Number.isSafeInteger(zero_observations) ||
        zero_observations < 0 ||
        zero_observations > observations
      ) {
        return null;
      }
      all.push({ date, region, channel, cost, observations, zero_observations });
    }
  }
  if (
    all.length !== sourceCount ||
    new Set(all.map((row) => JSON.stringify([row.date, row.region, row.channel]))).size !== all.length
  ) {
    return null;
  }
  return all;
}
function channelMetrics(cells: Array<ChannelCell>, period: string, market: string): Metrics {
  const values: Metrics = {};
  for (const cell of cells) {
    if (!cell.date.startsWith(period) || (market !== 'Global - All Markets' && cell.region !== market)) {
      continue;
    }
    const key = 'channel:' + cell.channel;
    values[key] = (values[key] ?? 0) + cell.cost;
  }
  return values;
}

function validFinancialCards(
  cards: ReadonlyArray<import('~/types/leLens').LensCardData>,
  required: Array<string>,
  comparison: boolean,
) {
  const schema: Record<string, Array<string>> = cardSchema;
  for (const id of required) {
    if (id === 'channel_cells' || id === 'targets') {
      continue;
    }
    const card = cards.find((card) => card.cardId === id);
    if (!card) {
      return false;
    }
    for (const result of comparison ? [card, card.comparison] : [card]) {
      if (!result) {
        return false;
      }
      const rows = result.rows ?? [];
      for (const row of rows) {
        for (const key of schema[id] ?? []) {
          const value = row[key];
          if (
            value !== null &&
            ((typeof value !== 'number' && typeof value !== 'string') ||
              (typeof value === 'string' && !value.trim()) ||
              numeric(value) === null)
          ) {
            return false;
          }
        }
        if (
          ['daily', 'monthly', 'global_monthly'].includes(id) &&
          !validDate(String(row.transaction_date_sydt).slice(0, 10))
        ) {
          return false;
        }
      }
    }
  }
  return true;
}
